import { useState, useEffect } from 'react'
import {
    Box,
    TextField,
    IconButton,
    Radio,
    List,
    ListItem,
    Typography,
    Menu,
    MenuItem,
    useTheme,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useRemindMeContext } from '../../context/RemindMeContext';
import { isValidEmail } from '../../utils/CommonMethods';
import DeleteConfirmationDialog from '../../components/remindMe/settings/DeleteConfirmationDialog';
import EditEmailDialog from '../../components/remindMe/settings/EditEmailDialog';
import InnerTopBar from '../../components/layout/InnerTopBar';
// import DeleteConfirmationDialogue from '../components/settings/DeleteConfirmationDialog';
// import EditEmailDialog from '../components/settings/EditEmailDialog';
// import { isValidEmail } from '../config/commonMethod';
// import { useAppContext } from '../context/AppContext';


export default function SettingsScreen() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [selectedRecipient, setSelectedRecipient] = useState<string | null>(null)
    const [email, setEmail] = useState("");
    const [emailList, setEmailList] = useState<string[]>([]);
    const [selectedDefaultEmail, setselectedDefaultEmail] = useState<string | null>(null);
    const [emailError, setEmailError] = useState("");
    const [deleteWarningMessage, setDeleteWarningMessage] = useState("");
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
    const [openEditConfirmation, setOpenEditConfirmation] = useState(false)
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const { updateEmail } = useRemindMeContext();
    const theme = useTheme()

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const loadEmails = () => {
            const storedEmails = localStorage.getItem('emails');
            const defaultEmail = localStorage.getItem('defaultEmail');
            if (storedEmails) {
                setEmailList(JSON.parse(storedEmails));
            }
            if (defaultEmail) {
                setselectedDefaultEmail(defaultEmail);
            }
        };
        loadEmails();
    }, []);

    // Set default email if emailList changes
    useEffect(() => {
        if (emailList.length > 0) {
            const defaultEmail = emailList[0];
            setselectedDefaultEmail(defaultEmail);
            saveDefaultEmail(defaultEmail);
        }
    }, [emailList]);

    const saveEmails = async (emails: string[]) => {
        try {
            localStorage.setItem("emails", JSON.stringify(emails));
        } catch (error) {
            console.error("Failed to save emails:", error);
        }
    };

    const saveDefaultEmail = async (email: string | null) => {
        try {
            // localStorage.setItem("defaultEmail", email || "");
            updateEmail(email ?? "");

        } catch (error) {
            console.error("Failed to save default email:", error);
        }
    };

    const handleAddEmail = async () => {
        const trimmedEmail = email.trim().toLowerCase();
        if (trimmedEmail) {
            if (!isValidEmail(trimmedEmail)) {
                setEmailError("Please enter a valid email address");
            } else if (emailList.includes(trimmedEmail)) {
                setEmailError("Duplicate address. Add new address.");
            } else {
                const updatedEmailList = [trimmedEmail, ...emailList];
                setEmailList(updatedEmailList);
                await saveEmails(updatedEmailList);
                setEmail("");
                setEmailError("");
                // if (!selectedEmail) {
                //     setSelectedEmail(trimmedEmail);
                // }
            }
        } else {
            setEmailError("Please type in an email address above before adding.");
        }
    };


    const handleRadioSelect = async (value: string) => {
        setselectedDefaultEmail(value);
        const updatedEmailList = emailList.filter((emailItem) => emailItem !== value);
        const newEmailList = [value, ...updatedEmailList];

        setEmailList(newEmailList);
        await saveEmails(newEmailList);
        await saveDefaultEmail(newEmailList[0]);
    };

    const handleDelete = () => {
        const warningMessage =
            emailList.length === 1
                ? "If you delete this address, there will be no email address to mail your RemindMes. Do you want to proceed?"
                : "Sure about deleting this email address?";
        setDeleteWarningMessage(warningMessage);
        setOpenDeleteConfirmation(true);
        setAnchorEl(null)
    };

    const confirmDelete = async () => {
        const updatedEmailList = emailList.filter((emailItem) => emailItem !== selectedRecipient);

        let newSelectedEmail: string | null = null;
        if (selectedDefaultEmail === selectedRecipient) {
            if (updatedEmailList.length > 0) {
                newSelectedEmail = updatedEmailList[0];
            } else {
                newSelectedEmail = null;
            }
        } else {
            newSelectedEmail = selectedDefaultEmail;
        }

        setEmailList(updatedEmailList);
        setselectedDefaultEmail(newSelectedEmail);

        await saveEmails(updatedEmailList);
        await saveDefaultEmail(newSelectedEmail);
        setOpenDeleteConfirmation(false);
        handleMenuClose()
    };

    const editEmailAction = async (email: string) => {
        const trimmedEmail = email.trim().toLowerCase();
        let updatedEmailList;
        if (selectedDefaultEmail) {
            updatedEmailList = emailList.map((item, index) =>
                index === editIndex ? trimmedEmail : item
            );
        } else {
            updatedEmailList = [trimmedEmail, ...emailList];
        }

        setEmailList(updatedEmailList);
        await saveEmails(updatedEmailList);
        setOpenEditConfirmation(false);
        handleMenuClose()
    }


    // Menu handling
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, recipient: string, index: number) => {
        setSelectedRecipient(recipient)
        setEditIndex(index)
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedRecipient(null)
        setEditIndex(null)
    }

    // Edit recipient
    const handleEditClick = () => {
        if (selectedRecipient) {
            setOpenEditConfirmation(true);
            setAnchorEl(null)
        }
    }

    return (
        <Box sx={{
            display: "flex", flexDirection: "column", width: "100%", flex: 1
        }}>
            <InnerTopBar title="Settings" />
            {/* Add new recipient */}
            <Box sx={{ display: 'flex', gap: 1, mt: 3, mb: 2, width: "100%" }}>
                <TextField
                    fullWidth
                    placeholder="Add an email address..."
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                    }
                    }
                    error={!!emailError}
                    helperText={emailError}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '40px',
                            backgroundColor: 'white',
                            height: '50px',
                            bgcolor: theme.palette.background.paper,
                        }
                    }}
                />
                <IconButton
                    onClick={handleAddEmail}
                    sx={{
                        color: 'primary.main',
                        bgcolor: 'transparent',
                        border: 'none',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        '&:hover': {
                            bgcolor: 'transparent',
                            opacity: 0.8
                        },
                        '&:focus': {
                            outline: 'none'
                        },
                        '&:active': {
                            outline: 'none'
                        },
                        fontSize: '50px'
                    }}
                >
                    <AddCircleOutlineIcon />
                </IconButton>
            </Box>

            {/* Recipients list */}
            <List >
                {emailList.map((email, index) => (
                    <ListItem
                        key={email}
                        sx={{
                            mb: 1,
                            bgcolor: theme.palette.background.paper,
                            borderRadius: 1,
                            '&:last-child': { mb: 0 }
                        }}
                    >
                        <Radio
                            checked={selectedDefaultEmail === email}
                            onChange={() => handleRadioSelect(email)}
                            sx={{
                                '&.Mui-checked': {
                                    color: '#6B4EFF'
                                }
                            }}
                        />
                        <Typography
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '& .MuiTypography-root': {
                                    textDecoration: 'none',
                                },
                            }}
                        >
                            {email}
                        </Typography>
                        <IconButton
                            edge="end"
                            onClick={(e) => handleMenuOpen(e, email, index)}
                            sx={{
                                '&:hover': {
                                    bgcolor: 'transparent',
                                    opacity: 0.8
                                },
                                '&:focus': {
                                    outline: 'none'
                                },
                                '&:active': {
                                    outline: 'none'
                                },
                            }}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </ListItem>
                ))}
            </List>

            {/* More options menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                open={openDeleteConfirmation}
                onClose={() => setOpenDeleteConfirmation(false)}
                onConfirm={confirmDelete}
                email={selectedRecipient ?? ""}
                message={deleteWarningMessage}
            />

            {/* Edit Email Popup */}
            <EditEmailDialog
                open={openEditConfirmation}
                onClose={() => setOpenEditConfirmation(false)}
                email={selectedRecipient ?? ""}
                onUpdate={editEmailAction}
                emailList={emailList}
            />
        </Box>
    )
}

